import { Outlet, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useSettings } from "@/context/SettingsContext";
import {
  Menu,
  X,
  MapPin,
  Instagram,
  Facebook,
  Twitter,
  Youtube,
  Phone,
} from "lucide-react";

import FloatingWhatsapp from "@/components/FloatingWhatsapp";
import MobileBottomNav from "@/components/MobileBottomNav";
import TrendingPopup from "@/components/TrendingPopup";
import InquiryDialog from "@/components/InquiryDialog";
import { Button } from "@/components/ui/button";
import { track } from "@/lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/destinations", label: "Destinations" },
  { to: "/services", label: "Services" },
  { to: "/transportation", label: "Vehicles" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

function Header() {
  const { settings } = useSettings();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);

    onScroll();

    window.addEventListener("scroll", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (pathname === "/") {
      track("homepage_view");
    }
  }, [pathname]);

  const isHomeHero = pathname === "/" && !scrolled;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background,backdrop-filter,border-color] duration-300 ${
        scrolled || !isHomeHero
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      {/* Header container */}
      <div className="max-w-7xl mx-auto px-4 lg:px-10 h-20 lg:h-24 flex items-center justify-between">

        {/* Brand */}
        <Link
          to="/"
          className="flex items-center gap-2 lg:gap-3 min-w-0"
          data-testid="brand-link"
        >
          {settings?.logo_url ? (
            <div className="flex items-center gap-2 lg:gap-3 min-w-0">

              {/* Logo */}
<div className="h-14 w-14 lg:h-20 lg:w-20 rounded-full bg-white shadow-md ring-1 ring-white/40 shrink-0 flex items-center justify-center overflow-hidden">
  <img
    src={settings.logo_url}
    alt={
      settings?.company_name ||
      "Vizag Jagan Tours & Travels"
    }
    className="w-[88%] h-[88%] object-contain"
    data-testid="brand-logo-img"
  />
</div>

              {/* Company name + tagline */}
              <div className="leading-tight min-w-0">
                <div
                  className={`font-display text-sm sm:text-base lg:text-xl truncate ${
                    isHomeHero
                      ? "text-white"
                      : "text-slate-900"
                  }`}
                >
                  {settings?.company_name ||
                    "Vizag Jagan Tours & Travels"}
                </div>

                <div
                  className={`text-[8px] sm:text-[9px] lg:text-[10px] uppercase tracking-[0.18em] ${
                    isHomeHero
                      ? "text-white/70"
                      : "text-slate-500"
                  }`}
                >
                  {settings?.tagline ||
                    "Travel with Comfort, Explore with Us"}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div
                className={`w-11 h-11 rounded-full grid place-items-center border ${
                  isHomeHero
                    ? "border-white/60"
                    : "border-slate-900/20"
                }`}
              >
                <span
                  className={`font-display text-xl ${
                    isHomeHero
                      ? "text-white"
                      : "text-slate-900"
                  }`}
                >
                  {(settings?.company_name || "V")[0]}
                </span>
              </div>

              <div className="leading-tight">
                <div
                  className={`font-display text-sm sm:text-base lg:text-lg ${
                    isHomeHero
                      ? "text-white"
                      : "text-slate-900"
                  }`}
                >
                  {settings?.company_name ||
                    "Vizag Jagan Tours & Travels"}
                </div>

                <div
                  className={`text-[8px] sm:text-[9px] lg:text-[10px] uppercase tracking-[0.18em] ${
                    isHomeHero
                      ? "text-white/70"
                      : "text-slate-500"
                  }`}
                >
                  {settings?.tagline ||
                    "Travel · Transportation"}
                </div>
              </div>
            </>
          )}
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-7 ml-auto">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={`text-sm font-medium tracking-wide transition-colors ${
                isHomeHero
                  ? "text-white/85 hover:text-white"
                  : "text-slate-700 hover:text-slate-950"
              } ${
                pathname === n.to
                  ? "!text-amber-500"
                  : ""
              }`}
            >
              {n.label}
            </Link>
          ))}

          {/* Desktop Book Now */}
          <InquiryDialog
            defaultMessage="Hello! I'd like to book a journey — please share your best options."
            source="header_book_now"
            triggerTestId="header-book-now-btn"
          >
            <Button
              data-testid="header-book-now-btn"
              className={`ml-2 rounded-full px-5 py-2 text-sm font-semibold shadow-sm whitespace-nowrap transition-colors ${
                isHomeHero
                  ? "bg-brand-navy-800 hover:bg-brand-navy-700 text-white"
                  : "bg-brand-navy-900 hover:bg-brand-navy-800 text-white"
              }`}
            >
              Book Now
            </Button>
          </InquiryDialog>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className={`lg:hidden shrink-0 p-2 ${
            isHomeHero
              ? "text-white"
              : "text-slate-900"
          }`}
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? (
            <X className="w-6 h-6" />
          ) : (
            <Menu className="w-6 h-6" />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200 shadow-lg">
          <div className="px-6 py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="text-slate-800 font-medium py-2"
                data-testid={`mnav-${n.label.toLowerCase()}`}
              >
                {n.label}
              </Link>
            ))}

            <InquiryDialog
              defaultMessage="Hello! I'd like to book a journey — please share your best options."
              source="header_book_now_mobile"
              triggerTestId="mnav-book-now-btn"
            >
              <Button
                data-testid="mnav-book-now-btn"
                className="mt-2 w-full rounded-full bg-brand-navy-800 hover:bg-brand-navy-700 text-white"
              >
                Book Now
              </Button>
            </InquiryDialog>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  const { settings } = useSettings();
  const s = settings || {};

  return (
    <footer className="relative bg-brand-navy-950 text-slate-300 mt-32 overflow-hidden">
      <div className="grain" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-12 gap-12 relative">

        {/* Footer Brand */}
        <div className="md:col-span-5">

          {/* Logo + Company Name */}
          <div className="flex items-center gap-4 sm:gap-5">
            {s.logo_url && (
              <img
                src={s.logo_url}
                alt={s.company_name || "Vizag Jagan Tours & Travels"}
                className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 object-contain"
                data-testid="footer-logo-img"
              />
            )}

            <div className="min-w-0">
              <div className="font-display text-2xl sm:text-3xl text-white leading-tight">
                {s.company_name || "Vizag Jagan Travels"}
              </div>
            </div>
          </div>

          <p className="mt-5 text-slate-400 leading-relaxed max-w-md">
            {s.tagline || "Vizag · Andhra · India"} — designed for
            those who prefer the road less rushed.
          </p>

          <div className="mt-6 flex items-center gap-4 text-slate-400">

            {s.social?.instagram && (
              <a
                href={s.social.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid="social-ig"
              >
                <Instagram className="w-4 h-4" />
              </a>
            )}

            {s.social?.facebook && (
              <a
                href={s.social.facebook}
                target="_blank"
                rel="noreferrer"
                data-testid="social-fb"
              >
                <Facebook className="w-4 h-4" />
              </a>
            )}

            {s.social?.twitter && (
              <a
                href={s.social.twitter}
                target="_blank"
                rel="noreferrer"
                data-testid="social-tw"
              >
                <Twitter className="w-4 h-4" />
              </a>
            )}

            {s.social?.youtube && (
              <a
                href={s.social.youtube}
                target="_blank"
                rel="noreferrer"
                data-testid="social-yt"
              >
                <Youtube className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>

        {/* Navigation */}
        <div className="md:col-span-3">
          <div className="text-xs uppercase tracking-[0.24em] text-amber-400 mb-4">
            Navigate
          </div>

          <ul className="space-y-2 text-sm">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link
                  to={n.to}
                  className="hover:text-white"
                >
                  {n.label}
                </Link>
              </li>
            ))}

            <li>
              <Link
                to="/faqs"
                className="hover:text-white"
              >
                FAQs
              </Link>
            </li>

            <li>
              <Link
                to="/testimonials"
                className="hover:text-white"
              >
                Testimonials
              </Link>
            </li>
          </ul>
        </div>

        {/* Contact */}
        <div className="md:col-span-4">
          <div className="text-xs uppercase tracking-[0.24em] text-amber-400 mb-4">
            Contact
          </div>

          <ul className="space-y-3 text-sm text-slate-400">

            {s.phone_primary && (
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-0.5" />

                <a
                  href={`tel:${s.phone_primary}`}
                  className="hover:text-white"
                >
                  {s.phone_primary}
                </a>
              </li>
            )}

            {s.address && (
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5" />

                <span>{s.address}</span>
              </li>
            )}

            {s.email && (
              <li className="flex items-start gap-3">
                <span className="w-4 h-4 inline-block" />

                @

                <a
                  href={`mailto:${s.email}`}
                  className="hover:text-white"
                >
                  {s.email}
                </a>
              </li>
            )}
          </ul>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between text-xs text-slate-500 gap-3">

          <div>
            © {new Date().getFullYear()}{" "}
            {s.company_name || "Vizag Jagan Travels"}.
            All rights reserved.
          </div>

          <div className="flex items-center gap-6">
            <Link
              to="/privacy"
              className="hover:text-white"
            >
              Privacy Policy
            </Link>

            <Link
              to="/terms"
              className="hover:text-white"
            >
              Terms & Conditions
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function PublicLayout() {
  return (
    <>
      <Header />

      <main className="pt-0">
        <Outlet />
      </main>

      <Footer />

      <FloatingWhatsapp />

      <MobileBottomNav />

      <TrendingPopup />
    </>
  );
}